import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { constructUrl, getParams } from "@/app/_module/services/helper";
import { shouldUseDryRun } from "@/app/_module/utils/environment";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { handleApiCall, extractApiData } from "@/app/_module/utils/apiResponseHandler";

import type { 
  GetSubscriptionPlansParams, 
  GetSubscriptionPlansResponse, 
  CheckoutSessionResponse,
  CompleteDryRunSubscriptionParams,
  CompleteDryRunSubscriptionResponse,
  GetMyActiveSubscriptionResponse
} from "../types/subscription-plans";

// Get all subscription plans with pagination - Settings context shows all plans including Enterprise
export const getSubscriptionPlans = async (param: GetSubscriptionPlansParams) => {
  let params;
  if (param) {
    params = getParams<GetSubscriptionPlansParams>(param);
  }

  // For settings, use the plans/available endpoint with context=settings to get all plans including Enterprise
  const baseUrl = getApiUrl(endpoint.plans.getAvailablePlans);
  const contextParam = 'context=settings';
  
  let url;
  if (params) {
    const existingParams = new URLSearchParams(params as Record<string, string>);
    existingParams.set('context', 'settings');
    url = `${baseUrl}?${existingParams.toString()}`;
  } else {
    url = `${baseUrl}?${contextParam}`;
  }

  try {
    const plansData = await handleApiCall(async () => {
      const response = await api.get<any>({ 
        url,
        auth: true // Add authentication
      });
      return response;
    });
    
    // Handle null or undefined response
    if (!plansData) {
      return [];
    }
    
    // Filter function to exclude trial plan (only for signup, not settings)
    // Trial plans have either isTrial: true or value/type/id === 'trial'
    const filterTrialPlan = (plan: any) => {
      // Check if it's a trial plan
      if (plan.isTrial === true) return false;

      const planValue = plan.value || plan.type || plan.id;
      if (planValue && planValue.toString().toLowerCase().includes('trial')) return false;

      return true;
    };

    // IMPORTANT: handleApiCall already extracts response.data via extractApiData()
    // Backend returns: {statusCode, message, data: {plans: [...], totalPlans, ...}}
    // handleApiCall extracts and returns: {plans: [...], totalPlans, ...}

    // Handle standard format: plans array (current backend format - already unwrapped by handleApiCall)
    if (plansData?.plans && Array.isArray(plansData.plans)) {
      return plansData.plans
        .filter(filterTrialPlan)
        .map(transformPlanFromBackend);
    }

    // Handle paginated format: items array
    if (plansData?.items && Array.isArray(plansData.items)) {
      const filteredItems = plansData.items
        .filter(filterTrialPlan)
        .map(transformPlanFromBackend);
      return {
        ...plansData,
        items: filteredItems
      };
    }

    // If response is direct array
    if (Array.isArray(plansData)) {
      return plansData
        .filter(filterTrialPlan)
        .map(transformPlanFromBackend);
    }

    // If no recognizable structure, return empty array
    return [];
  } catch (error) {
    // Return empty array instead of throwing to prevent UI crashes
    return [];
  }
};

// Transform backend response to match frontend interface
function transformPlanFromBackend(plan: any): any {
  // If already in correct format, return as-is
  if (plan.monthlyPrice !== undefined && plan.yearlyPrice !== undefined) {
    return plan;
  }
  
  // Parse features if it's a JSON string
  let features = [];
  if (typeof plan.features === 'string') {
    try {
      features = JSON.parse(plan.features);
    } catch {
      features = [];
    }
  } else if (Array.isArray(plan.features)) {
    features = plan.features;
  } else if (plan.features && typeof plan.features === 'object') {
    features = Object.values(plan.features);
  }
  
  // Transform snake_case to camelCase (matching exact database structure)
  const monthlyPrice = plan.monthly_price || plan.monthlyPrice || plan.price || 0;
  const planValue = plan.value || plan.type || plan.id;

  return {
    id: plan.id, // Use database UUID directly for proper plan lookup in upgrade endpoint
    name: plan.label || plan.name,
    description: plan.description || '',
    price: monthlyPrice,
    monthlyPrice: monthlyPrice,
    yearlyPrice: monthlyPrice * 12, // Calculate yearly from monthly
    currency: plan.currency || 'USD',
    // Only Basic and Full-Fledged have Stripe price IDs (not Enterprise)
    providerPriceId: planValue === 'basic' || planValue === 'full_fledged' 
      ? `price_${planValue}` 
      : null,
    providerName: planValue === 'basic' || planValue === 'full_fledged' 
      ? 'stripe' 
      : null,
    features: features,
    active: plan.is_selectable !== undefined ? plan.is_selectable : true,
    type: planValue,
    trialAvailable: false, // No trial in settings view
    trialDuration: null,
    canSelectAgencyOwner: plan.is_selectable,
    createdAt: plan.created_at || plan.createdAt,
    updatedAt: plan.updated_at || plan.updatedAt,
    deletedAt: plan.deleted_at || plan.deletedAt || null,
  };
}

// Create a checkout session for a subscription plan
export const createCheckoutSession = async (planId: string, forceDryRun?: boolean) => {
  // Always use real Stripe checkout (no dry run) unless explicitly overridden
  const dryRun = forceDryRun !== undefined ? forceDryRun : false;

  const url = `${getApiUrl(endpoint.subscriptions.checkoutSession)}?dryRun=${dryRun}`;
  try {
    const response = await api.post<CheckoutSessionResponse>({
      url,
      body: { subscriptionPlanId: planId },
      auth: true // Add authentication
    });
    showToast("Checkout session created successfully!", "success");
    return response.data;

  } catch (error) {
    throw error;
  }
};

// Complete dry run subscription
export const completeDryRunSubscription = async (params: CompleteDryRunSubscriptionParams) => {
  // Only allow dry run completion in non-production environments
  if (process.env.NODE_ENV === 'production') {

    return null;
  }

  const url = getApiUrl(endpoint.subscriptions.completeDryRunSubscription);
  try {
    const response = await api.post<CompleteDryRunSubscriptionResponse>({
      url,
      body: params,
    });
    return response.data;

  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw error;
  }
};

// Get user's active subscription
export const getMyActiveSubscription = async () => {
  const url = getApiUrl(endpoint.subscriptions.getMyActiveSubscription);
  try {
    return await handleApiCall<GetMyActiveSubscriptionResponse>(async () => {
      const response = await api.get<GetMyActiveSubscriptionResponse>({
        url,
        auth: true // Add authentication
      });
      return response;
    });
  } catch (error) {
    throw error;
  }
};

// Upgrade subscription to a new plan (for users with active subscriptions)
export const upgradeSubscription = async (newPlanId: string, forceDryRun?: boolean) => {
  // Always use real Stripe checkout (no dry run) unless explicitly overridden
  const dryRun = forceDryRun !== undefined ? forceDryRun : false;

  const url = `${getApiUrl(endpoint.subscriptions.upgrade)}?dryRun=${dryRun}`;
  try {
    const response = await api.post<CheckoutSessionResponse>({
      url,
      body: { newPlanId },
      auth: true
    });
    showToast("Upgrade checkout session created successfully!", "success");
    return response.data;

  } catch (error) {
    throw error;
  }
};

// Get usage stats (leads and dial time)
export const getUsageStats = async () => {
  const url = getApiUrl(endpoint.subscriptions.getUsageStats);
  try {
    return await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url,
        auth: true
      });
      return response;
    });
  } catch (error) {
    throw error;
  }
};